import { useEffect, useMemo, useState } from "react";
import { Box, Card, Stack, Typography, IconButton, TextField, Button, Divider, CircularProgress, Tooltip, Pagination, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import { Delete, Edit, Save, Close, Refresh, Add } from "@mui/icons-material";
import { alpha, styled, useTheme } from "@mui/material/styles";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { useToast } from "OtherReusableFunctionality/ToastContext";
import { usePageTitle } from "contexts/PageTitleContext";
import { useDeleteConfirmation } from "contexts/DeleteConfirmationContext";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Chip,

} from "@mui/material";
const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: "0.875rem",
  padding: "12px 16px",
  "&.header": {
    fontWeight: 600,
    backgroundColor: theme.palette.grey[100],
  }
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  }
}));

const TableCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const Row = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '64px 1fr 160px 240px',
  alignItems: 'center',
  gap: theme.spacing(2),
  padding: theme.spacing(1.5, 0),
}));

const HeaderRow = styled(Row)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontWeight: 600,
}));

const Muted = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

const ActionWrap = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  justifyContent: 'flex-end',
}));

const CropTypesPage = () => {
  const theme = useTheme();
  const { showSuccess, showError } = useToast();
  // const { setPageTitle } = usePageTitle();
  const { showDeleteConfirmation } = useDeleteConfirmation();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [editing, setEditing] = useState({}); // { [id]: { name: string, sequence: number, isEditingName: boolean } }
  const [creating, setCreating] = useState(false);
  const [newCrop, setNewCrop] = useState({ name: '', sequence: '' });
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 1,
    has_next: false,
    has_previous: false,
    next_page_number: null,
    previous_page_number: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [10, 25, 50, 100];
   const { setPageTitle } = usePageTitle();
  useEffect(() => {
        setPageTitle('Crop types');
    }, [setPageTitle]);
  const userId = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("logged_data"))?.data?.id || JSON.parse(localStorage.getItem("logged_data"))?.data?.user_id || 1;
    } catch (_) {
      return 1;
    }
  }, []);

  const fetchItems = async (page = currentPage, pageSizeParam = pageSize) => {
    setLoading(true);
    try {
      const url = `${API_ENDPOINTS.GET_admin_crop_types}?user_id=${userId}&page=${page}&page_size=${pageSizeParam}`;
      const res = await apiService.get(url);
      const data = Array.isArray(res?.data) ? res.data : (res?.data?.data ?? []);
      setItems(data);
      const paginationData = res?.pagination || res?.data?.pagination || {};
      if (Object.keys(paginationData).length > 0) {
        setPagination({
          current_page: paginationData.current_page ?? page,
          page_size: paginationData.page_size ?? pageSizeParam,
          total_items: paginationData.total_items ?? 0,
          total_pages: paginationData.total_pages ?? 1,
          has_next: paginationData.has_next ?? false,
          has_previous: paginationData.has_previous ?? false,
          next_page_number: paginationData.next_page_number ?? null,
          previous_page_number: paginationData.previous_page_number ?? null,
        });
        setCurrentPage(paginationData.current_page ?? page);
        setPageSize(paginationData.page_size ?? pageSizeParam);
      } else {
        setPagination(prev => ({
          ...prev,
          total_items: data.length,
          total_pages: 1,
          current_page: 1,
          page_size: pageSizeParam,
        }));
      }
    } catch (err) {
      showError(typeof err === 'string' ? err : err?.message || 'Failed to load crop types');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(1, pageSize);
  }, []);

  const handlePageChange = (_, value) => {
    setCurrentPage(value);
    fetchItems(value, pageSize);
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
    fetchItems(1, newSize);
  };

  const createCrop = async () => {
    if (!newCrop.name || !String(newCrop.name).trim()) {
      showError('Crop name cannot be empty');
      return;
    }
    const seq = Number(newCrop.sequence);
    if (Number.isNaN(seq)) {
      showError('Sequence must be a number');
      return;
    }

    setCreating(true);
    try {
      const formData = new FormData();
      formData.append('name', String(newCrop.name).trim());
      formData.append('sequence', String(seq));

      const res = await apiService.post(API_ENDPOINTS.POST_create_crop, formData, undefined, 'multipart/form-data');
      showSuccess(res?.message || 'Crop type created successfully');
      setNewCrop({ name: '', sequence: '' });
      await fetchItems();
    } catch (err) {
      showError(typeof err === 'string' ? err : err?.message || 'Failed to create crop type');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (it) => {
    setEditing((prev) => ({
      ...prev,
      [it.id]: { name: it.name, sequence: it.sequence, isEditingName: true },
    }));
  };

  const cancelEdit = (id) => {
    setEditing((prev) => {
      const n = { ...prev };
      delete n[id];
      return n;
    });
  };

  const saveName = async (id) => {
    const state = editing[id];
    if (!state) return;
    if (!state.name || !String(state.name).trim()) {
      showError('Crop name cannot be empty');
      return;
    }
    setSavingId(id);
    try {
      // Save name
      const formData = new FormData();
      formData.append('crop_id', String(id));
      formData.append('name', String(state.name).trim());
      formData.append('user_id', String(userId));

      const res = await apiService.put(API_ENDPOINTS.UPDATE_admin_crop_types, formData, undefined, 'multipart/form-data');
      
      // Also save sequence if it has been changed
      const originalItem = items.find((x) => x.id === id);
      const seq = Number(state.sequence);
      if (!Number.isNaN(seq) && originalItem && seq !== originalItem.sequence) {
        const seqFormData = new FormData();
        seqFormData.append('crop_id', String(id));
        seqFormData.append('sequence', String(seq));
        await apiService.put(API_ENDPOINTS.UPDATE_admin_crop_types_sequence, seqFormData, undefined, 'multipart/form-data');
      }

      showSuccess(res?.message || 'Crop type updated');
      await fetchItems();
      cancelEdit(id);
    } catch (err) {
      showError(typeof err === 'string' ? err : err?.message || 'Failed to update');
    } finally {
      setSavingId(null);
    }
  };

  const saveSequence = async (id) => {
    const state = editing[id] || { sequence: items.find((x) => x.id === id)?.sequence };
    const seq = Number(state.sequence);
    if (Number.isNaN(seq)) {
      showError('Sequence must be a number');
      return;
    }
    setSavingId(id);
    try {
      const formData = new FormData();
      formData.append('crop_id', String(id));
      formData.append('sequence', String(seq));

      const res = await apiService.put(API_ENDPOINTS.UPDATE_admin_crop_types_sequence, formData, undefined, 'multipart/form-data');
      showSuccess(res?.message || 'Sequence updated');
      await fetchItems();
    } catch (err) {
      showError(typeof err === 'string' ? err : err?.message || 'Failed to update sequence');
    } finally {
      setSavingId(null);
    }
  };

  const removeItem = async (id) => {
    const confirmed = await showDeleteConfirmation({
      title: "Delete Crop Type",
      message: "Are you sure you want to delete this crop type?",
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel"
    });
    
    if (!confirmed) return;
    setDeletingId(id);
    try {
      const res = await apiService.delete(`${API_ENDPOINTS.DELETE_admin_crop_types}?user_id=${userId}&crop_id=${id}`);
      showSuccess(res?.message || 'Crop type deleted');
      await fetchItems();
    } catch (err) {
      showError(typeof err === 'string' ? err : err?.message || 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <PageContainer>
      <HeaderCard>
        <Box>
          <Typography variant="h6" fontWeight={700}>Crop Types</Typography>
          <Muted variant="body2">View, rename, resequence, and delete crop types</Muted>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* <Button startIcon={<Add />} variant="contained" onClick={createCrop} disabled={creating}>
            {creating ? 'Creating...' : 'Add Crop'}
          </Button> */}
          <Button startIcon={<Refresh />} variant="outlined" onClick={fetchItems} disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Box>
      </HeaderCard>
      

      {/* Add Crop Form */}
      <TableCard sx={{ mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}></Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Crop Name"
            size="small"
            value={newCrop.name}
            onChange={(e) => setNewCrop(prev => ({ ...prev, name: e.target.value }))}
            sx={{ minWidth: 200 }}
          />
          <TextField
            label="Sequence"
            size="small"
            type="number"
            value={newCrop.sequence}
            onChange={(e) => setNewCrop(prev => ({ ...prev, sequence: e.target.value }))}
            sx={{ width: 120 }}
          />
          <Button 
            variant="contained" 
            onClick={createCrop} 
            disabled={creating || !newCrop.name.trim() || !newCrop.sequence}
            startIcon={creating ? <CircularProgress size={16} /> : <Add />}
          >
            {creating ? 'Creating...' : 'Create'}
          </Button>
          <FormControl size="small" sx={{ minWidth: 110,justifyContent: 'flex-end', marginLeft: 'auto'  }}>
        <InputLabel>Rows</InputLabel>
        <Select value={pageSize} label="Rows" onChange={handlePageSizeChange}>
          {pageSizeOptions.map((opt) => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </Select>
      </FormControl>
        </Box>
      </TableCard>


      {/* <TableCard>
        <HeaderRow>
          <Muted variant="caption">Sr No</Muted>
          <Muted variant="caption">Crop Name</Muted>
          <Muted variant="caption">Sequence</Muted>
          <Muted variant="caption" sx={{ textAlign: 'right' }}>Actions</Muted>
        </HeaderRow>
        <Divider />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : items.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Muted>No crop types found.</Muted>
          </Box>
        ) : (
          items.map((it, index) => {
            const isEditing = !!editing[it.id];
            return (
              <Box key={it.id}>
                <Row>
                  <Typography variant="body2">{index + 1}</Typography>

                  {isEditing ? (
                    <TextField
                      size="small"
                      fullWidth
                      value={editing[it.id]?.name ?? it.name ?? ''}
                      onChange={(e) => setEditing((prev) => ({ 
                        ...prev, 
                        [it.id]: { 
                          name: e.target.value, 
                          sequence: prev[it.id]?.sequence ?? it.sequence 
                        } 
                      }))}
                    />
                  ) : (
                    <Typography variant="body1" fontWeight={600}>{it.name}</Typography>
                  )}

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TextField
                      size="small"
                      type="number"
                      disabled={savingId === it.id}
                      sx={{ width: 100 }}
                      value={isEditing ? (editing[it.id]?.sequence ?? it.sequence ?? '') : (it.sequence ?? '')}
                      onChange={(e) => {
                        if (isEditing) {
                          setEditing((prev) => ({ 
                            ...prev, 
                            [it.id]: { 
                              name: prev[it.id]?.name ?? it.name, 
                              sequence: e.target.value 
                            } 
                          }));
                        } else {
                          setEditing((prev) => ({ 
                            ...prev, 
                            [it.id]: { 
                              name: it.name, 
                              sequence: e.target.value 
                            } 
                          }));
                        }
                      }}
                    />
                    {!isEditing && (
                      <Button size="small" variant="contained" onClick={() => saveSequence(it.id)} disabled={savingId === it.id}>
                        {savingId === it.id ? 'Saving...' : 'Update'}
                      </Button>
                    )}
                  </Box>

                  <ActionWrap>
                    {isEditing ? (
                      <>
                        <Tooltip title="Save name and sequence">
                          <span>
                            <IconButton color="success" onClick={() => saveName(it.id)} disabled={savingId === it.id}>
                              <Save />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton color="inherit" onClick={() => cancelEdit(it.id)}>
                            <Close />
                          </IconButton>
                        </Tooltip>
                      </>
                    ) : (
                      <Tooltip title="Rename">
                        <IconButton color="primary" onClick={() => startEdit(it)}>
                          <Edit />
                        </IconButton>
                      </Tooltip>
                    )}

                    <Tooltip title="Delete">
                      <span>
                        <IconButton color="error" onClick={() => removeItem(it.id)} disabled={deletingId === it.id}>
                          {deletingId === it.id ? <CircularProgress size={18} /> : <Delete />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </ActionWrap>
                </Row>
                <Divider />
              </Box>
            );
          })
        )}
      </TableCard> */}

      <TableCard sx={{ mb: 2 }}>
  <Box sx={{ width: "100%", p: 2 }}>
    <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: "auto", maxHeight: 500, /* Firefox */
            scrollbarWidth: "thin",
            scrollbarColor:
              theme.palette.mode === "dark"
                ? "#555 #1e1e1e"
                : "#c1c1c1 #f1f1f1",

            /* Chrome, Edge, Safari */
            "&::-webkit-scrollbar": {
              height: 8,
              width: 8,
            },
            "&::-webkit-scrollbar-track": {
              background:
                theme.palette.mode === "dark"
                  ? "#1e1e1e"
                  : "#f1f1f1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb": {
              background:
                theme.palette.mode === "dark"
                  ? "#555"
                  : "#c1c1c1",
              borderRadius: 8,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              background:
                theme.palette.mode === "dark"
                  ? "#777"
                  : "#a8a8a8",
            }, }}>
      <Table stickyHeader>
        {/* ---------- TABLE HEADER ---------- */}
        <TableHead sx={{ position: 'sticky', top: 0, zIndex: 20 }}>
          <TableRow>
            <StyledTableCell className="header" style={{ padding: "16px" }}>Sr No</StyledTableCell>
            <StyledTableCell className="header">Crop Name</StyledTableCell>
            <StyledTableCell className="header">Sequence</StyledTableCell>
            <StyledTableCell className="header">Actions</StyledTableCell>
          </TableRow>
        </TableHead>

        {/* ---------- TABLE BODY ---------- */}
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                <CircularProgress size={28} />
              </TableCell>
            </TableRow>
          ) : items.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                <Typography variant="body2" color="text.secondary">
                  No crop types found.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            items.map((it, index) => {
              const isEditing = !!editing[it.id];

              return (
                <StyledTableRow key={it.id}>
                  {/* ---------- SR NO ---------- */}
                  <StyledTableCell style={{ padding: "16px" }}>
                    <Chip
                      label={`#${(pagination.current_page - 1) * pagination.page_size + index + 1}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ fontWeight: 600 }}
                    />
                  </StyledTableCell>

                  {/* ---------- NAME ---------- */}
                  <StyledTableCell>
                    {isEditing ? (
                      <TextField
                        size="small"
                        fullWidth
                        value={editing[it.id]?.name ?? it.name ?? ""}
                        onChange={(e) =>
                          setEditing((prev) => ({
                            ...prev,
                            [it.id]: {
                              name: e.target.value,
                              sequence: prev[it.id]?.sequence ?? it.sequence,
                            },
                          }))
                        }
                      />
                    ) : (
                      <Typography variant="subtitle2" fontWeight={600}>
                        {it.name}
                      </Typography>
                    )}
                  </StyledTableCell>

                  {/* ---------- SEQUENCE ---------- */}
                  <StyledTableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <TextField
                        size="small"
                        type="number"
                        sx={{ width: 100 }}
                        disabled={savingId === it.id}
                        value={
                          isEditing
                            ? editing[it.id]?.sequence ?? it.sequence ?? ""
                            : it.sequence ?? ""
                        }
                        onChange={(e) =>
                          setEditing((prev) => ({
                            ...prev,
                            [it.id]: {
                              name: prev[it.id]?.name ?? it.name,
                              sequence: e.target.value,
                            },
                          }))
                        }
                      />

                      {!isEditing && (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => saveSequence(it.id)}
                          disabled={savingId === it.id}
                        >
                          {savingId === it.id ? "Saving..." : "Update"}
                        </Button>
                      )}
                    </Box>
                  </StyledTableCell>

                  {/* ---------- ACTIONS ---------- */}
                  <StyledTableCell >
                    <Box display="flex" gap={1} >
                      {isEditing ? (
                        <>
                          <Tooltip title="Save">
                            <span>
                              <IconButton
                                color="success"
                                onClick={() => saveName(it.id)}
                                disabled={savingId === it.id}
                              >
                                <Save />
                              </IconButton>
                            </span>
                          </Tooltip>

                          <Tooltip title="Cancel">
                            <IconButton color="inherit" onClick={() => cancelEdit(it.id)}>
                              <Close />
                            </IconButton>
                          </Tooltip>
                        </>
                      ) : (
                        <Tooltip title="Edit">
                          <IconButton color="primary" onClick={() => startEdit(it)}>
                            <Edit />
                          </IconButton>
                        </Tooltip>
                      )}

                      <Tooltip title="Delete">
                        <span>
                          <IconButton
                            color="error"
                            onClick={() => removeItem(it.id)}
                            disabled={deletingId === it.id}
                          >
                            {deletingId === it.id ? (
                              <CircularProgress size={18} />
                            ) : (
                              <Delete />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </StyledTableCell>
                </StyledTableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
  {/* Pagination and row dropdown */}
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, flexWrap: 'wrap', gap: 2 }}>
    {/* <Typography variant="body2" color="text.secondary">
      {pagination.total_items > 0
        ? `Showing ${(pagination.current_page - 1) * pagination.page_size + 1} - ${Math.min(pagination.current_page * pagination.page_size, pagination.total_items)} of ${pagination.total_items}`
        : 'No crop types'}
    </Typography> */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      {/* <FormControl size="small" sx={{ minWidth: 110 }}>
        <InputLabel>Rows</InputLabel>
        <Select value={pageSize} label="Rows" onChange={handlePageSizeChange}>
          {pageSizeOptions.map((opt) => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </Select>
      </FormControl> */}
      {/* {pagination.total_pages > 1 && (
        <Pagination count={pagination.total_pages} page={currentPage} onChange={handlePageChange} color="primary" />
      )} */}
    </Box>
    <Box
  sx={{
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    mt: 2,
    width: "100%",
    gap: 2,
  }}
>
  
  {pagination.total_pages > 1 && (
    <Pagination
      count={pagination.total_pages}
      page={currentPage}
      onChange={handlePageChange}
      color="primary"
    />
  )}
  <Typography variant="body2" color="text.secondary">
    {pagination.total_items > 0
      ? `Showing ${(pagination.current_page - 1) * pagination.page_size + 1} - 
        ${Math.min(
          pagination.current_page * pagination.page_size,
          pagination.total_items
        )} of ${pagination.total_items}`
      : "No crop types"}
  </Typography>

</Box>
  </Box>
</TableCard>

    </PageContainer>
  );
};

export default CropTypesPage;